import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator";
import draggable from "vuedraggable";
import { Button, Col, List, Row, Spin, Table, Modal, Carousel, Icon, Empty } from "ant-design-vue";
import { getModule } from "vuex-module-decorators";
import { Drawer } from "@/components/drawer";
import {
	ContentExposureReaderRequestModel,
	ContentExposureService,
	ExposurePreviewContentModel,
	FocusWordItemModel,
	FocusWordModel,
	ManageOrderItemModel,
	PreviewContentsModel,
	PreviewItemContentModel,
	PreviewItemRequestModel
} from "@/services/content-exposure";
import { CommonLocale, ContentExposureManageOrderLocale } from "@/locales/localeid";
import TitleListState from "@/modules/title-list";
import { MessageHelper, NotificationType, fmtMsg } from "commonui";
import { AdminService, ContainerType, SignatureResoureModel } from "@/services/admin";
import { Moment } from "moment";
import moment from "moment";
import { MediaPlayerContentType, MediaSlot, MediaType, ReaderType } from "@/util/enum";
import IVideoJSPlayer from "@/components/videojsplayer/videojsplayer";
import VideoJSPlayer from "@/components/videojsplayer/videojsplayer.vue";
import { groupBy } from "lodash";

interface ReviewItemFoucsWordMedia {
	mediaSlotId: number;
	url: string;
	id: string;
}

interface ReviewItemPagesMedia {
	pageUrl: string;
	audioUrl: string;
	pageAudioId: string;
}

@Component({
	components: {
		Drawer,
		draggable,
		Button,
		Row,
		Col,
		List,
		ListItem: List.Item,
		Table,
		Spin,
		Modal,
		Carousel,
		Icon,
		VideoJSPlayer,
		Empty
	}
})
export default class ManageOrderDrawer extends Vue {
	@Prop({ default: () => false }) visible!: boolean;
	@Prop() onClose!: Function;
	@Prop({ default: "" }) contentExposureId!: string;
	@Prop({ default: "" }) selectedLpvId!: string;
	@Prop({ default: "" }) parentContentId!: string;
	@Ref("videoPlayerRef") videoPlayerRef!: IVideoJSPlayer;
	public titleListModule = getModule(TitleListState);
	public modalTitle: string = fmtMsg(ContentExposureManageOrderLocale.CEMOTitle);
	public allItems: ManageOrderItemModel[] = [];
	public selectedItems: ManageOrderItemModel[] = [];
	public loading: boolean = false;
	public isSaving: boolean = false;
	public isPreviewVisible: boolean = false;
	public previewItems: PreviewItemContentModel[] = [];
	downloadSASURL: SignatureResoureModel[] | null = null;
	downloadSASURLFetchAt: Moment | null = null;
	public currentPreviewItem: PreviewItemContentModel | null = null;
	public currentPreviewItemMedias: ReviewItemPagesMedia[] = [];
	public currentIndex: number = 1;
	public isMediaLoading: boolean = false;
	public isApplyOnAllLoading: boolean = false;
	public isFirstPreviewItem: boolean = true;
	public isLastPreviewtItem: boolean = false;
	public noPreviewItems: boolean = false;

	created() {
		this.getDownloadSasURL();
	}

	@Watch("contentExposureId")
	fetchData() {
		this.loading = true;
		const parentContentId = this.getParentContentId();

		if (parentContentId && this.contentExposureId) {
			this.fetchOrderItems(parentContentId);
		}
	}

	fetchOrderItems(parentContentId: string) {
		ContentExposureService.getManageOrderItemsForExposure(parentContentId, this.contentExposureId)
			.then((data) => {
				this.allItems = data.allItems;
				this.selectedItems = data.selectedItems.sort((a, b) => (a.sequence < b.sequence ? -1 : 1));
				this.loading = false;
			})
			.catch(() => {
				this.loading = false;
			});
	}

	public onDrawerClose() {
		this.onClose();
		this.allItems = [];
		this.selectedItems = [];
	}

	public log(e) {
		this.selectedItems = this.selectedItems.map((item, index) => {
			return { ...item, sequence: index + 1 };
		});
	}

	public onSave() {
		this.isSaving = true;
		const items: ContentExposureReaderRequestModel[] = this.selectedItems.map((selectedItem) => {
			return {
				contentExposureId: selectedItem.contentExposureId,
				contentId: selectedItem.contentId,
				readerTypeId: selectedItem.readerTypeId,
				sequence: selectedItem.sequence
			};
		});
		ContentExposureService.saveExposureItems(this.contentExposureId, items)
			.then(() => {
				this.isSaving = false;
				MessageHelper.Message(
					NotificationType.Success,
					this.formatMessage(ContentExposureManageOrderLocale.CEMOOrderSaveSuccessText)
				);
				this.onDrawerClose();
			})
			.catch(() => {
				this.isSaving = false;
			});
	}

	formatMessage(id: string, params?: any): string {
		return fmtMsg(id, params);
	}

	get contentExposureManageOrderLocale() {
		return ContentExposureManageOrderLocale;
	}

	get commonLocale() {
		return CommonLocale;
	}

	public onPreview() {
		this.isPreviewVisible = true;
		this.fetchPreviewItems();
	}

	public onPreviewClose() {
		this.isPreviewVisible = false;
		this.currentPreviewItem = null;
		this.currentPreviewItemMedias = [];
		this.currentIndex = 1;
		this.isMediaLoading = false;
		this.loading = false;
		this.isFirstPreviewItem = true;
		this.isLastPreviewtItem = false;
		this.previewItems = [];
		this.noPreviewItems = false;
	}

	fetchPreviewItems() {
		this.isMediaLoading = true;
		const previewContents = this.selectedItems.map((selectedItem) => {
			const item: PreviewContentsModel = {
				contentId: selectedItem.contentId,
				readerTypeId: selectedItem.readerTypeId,
				sequence: selectedItem.sequence
			};
			return item;
		});

		const request: PreviewItemRequestModel = {
			previewContents: previewContents
		};

		ContentExposureService.previewItems(this.contentExposureId, request).then(async (data) => {
			await this.getDownloadSasURL();
			let previewItems = data
				.filter(
					(item) =>
						((item.readerTypeId === ReaderType.ChapterContent ||
							item.readerTypeId === ReaderType.Content) &&
							item.medias.length) ||
						(item.readerTypeId === ReaderType.FocusWord && item.focusWordInfo)
				)
				.sort((a, b) => (a.sequence < b.sequence ? -1 : 1));

			const focusWordItems: FocusWordItemModel[] = this.getFocusWordItems(previewItems);
			let newPreviewItems: PreviewItemContentModel[] = [];

			previewItems = previewItems.filter((item) => item.readerTypeId !== ReaderType.FocusWord);
			newPreviewItems = previewItems.map((item) => {
				const newPreviewItem: PreviewItemContentModel = {
					contentId: item.contentId,
					medias: item.medias,
					sequence: item.sequence,
					focusWordInfo: [],
					readerTypeId: item.readerTypeId
				};

				return newPreviewItem;
			});

			focusWordItems.forEach((wordItem) => {
				const newItem: PreviewItemContentModel = {
					contentId: wordItem.contentId,
					medias: [],
					sequence: wordItem.sequence,
					readerTypeId: ReaderType.FocusWord,
					focusWordInfo: wordItem.focusWords
				};
				newPreviewItems.splice(wordItem.sequence - 1, 0, newItem);
			});

			this.previewItems = newPreviewItems;

			if (this.previewItems.length) {
				this.currentPreviewItem = this.previewItems[0];
				this.assignItemMedias();

				/*
				 *disable next navigation button if there is only one item &
				 *it has either only one page or it a foucus word, chapter audio, chapter video.
				 */
				if (
					this.previewItems.length === 1 &&
					((this.hasPreviewItemPages(this.currentPreviewItem) &&
						this.getItemMediasLength(this.currentPreviewItem) === 1) ||
						this.currentPreviewItem.readerTypeId === ReaderType.FocusWord)
				) {
					this.isLastPreviewtItem = true;
				}

				this.isMediaLoading = false;
			} else {
				this.noPreviewItems = true;
				this.isMediaLoading = false;
			}
		});
	}

	getFocusWordItems(previewItems: ExposurePreviewContentModel[]): FocusWordItemModel[] {
		const focusWordItems: FocusWordItemModel[] = [];
		const allFocusWordItems = previewItems.filter((item) => item.readerTypeId === ReaderType.FocusWord);
		const focusWordItemsGroupedByContentId = groupBy(allFocusWordItems, "contentId");
		Object.entries(focusWordItemsGroupedByContentId).forEach((item) => {
			const key = item[0];
			const values = item[1];

			const focusWords: FocusWordModel[] = values.map((value) => {
				const foucsWord: FocusWordModel = {
					name: value.focusWordInfo.name,
					definition: value.focusWordInfo.definition,
					sequence: value.sequence,
					medias: value.medias
				};
				return foucsWord;
			});

			const focusWordOfContent: FocusWordItemModel = {
				contentId: key,
				readerTypeId: ReaderType.FocusWord,
				sequence: values[0].sequence,
				focusWords: focusWords
			};

			focusWordItems.push(focusWordOfContent);
		});

		return focusWordItems;
	}
	async getDownloadSasURL() {
		// gets sas URL for uploads
		if (!this.downloadSASURL || moment(this.downloadSASURLFetchAt).diff(moment(), "h") > 1) {
			// fetch sas url
			this.setDownloadSasURL(await AdminService.getDownloadSasURL());
		}
	}

	setDownloadSasURL(value: SignatureResoureModel[]) {
		this.downloadSASURL = value;
		this.downloadSASURLFetchAt = moment();
	}

	showPrevious() {
		this.isLastPreviewtItem = false;
		const firstItem = this.previewItems[0];

		const currentPreviewItemIndex = this.previewItems.findIndex(
			(previewItem) =>
				previewItem.contentId === this.currentPreviewItem?.contentId &&
				previewItem.readerTypeId === this.currentPreviewItem.readerTypeId
		);

		const firstItemIndex = this.previewItems.findIndex(
			(previewItem) =>
				previewItem.contentId === firstItem.contentId && previewItem.readerTypeId === firstItem.readerTypeId
		);

		this.disablePreviousNavigationButton(currentPreviewItemIndex, firstItemIndex, firstItem);

		if (this.currentIndex === 1 || this.currentPreviewItem?.readerTypeId === ReaderType.FocusWord) {
			const currentPreviewItemIndex = this.previewItems.findIndex(
				(previewItem) =>
					previewItem.contentId === this.currentPreviewItem?.contentId &&
					previewItem.readerTypeId === this.currentPreviewItem.readerTypeId
			);

			this.currentPreviewItem = this.previewItems[currentPreviewItemIndex - 1];
			this.assignItemMedias();

			if (this.currentPreviewItemMedias.length) {
				this.currentIndex = this.currentPreviewItemMedias.length;
			}
		} else if (this.currentIndex > 1) {
			this.currentIndex = this.currentIndex - 1;
		}
	}

	showNext() {
		this.isFirstPreviewItem = false;
		const lastPreviewItem = this.previewItems[this.previewItems.length - 1];

		const currentPreviewItemIndex = this.previewItems.findIndex(
			(previewItem) =>
				previewItem.contentId === this.currentPreviewItem?.contentId &&
				previewItem.readerTypeId === this.currentPreviewItem.readerTypeId
		);

		const lastPreviewItemIndex = this.previewItems.findIndex(
			(previewItem) =>
				previewItem.contentId === lastPreviewItem.contentId &&
				previewItem.readerTypeId === lastPreviewItem.readerTypeId
		);

		this.disableNextNavigationButton(currentPreviewItemIndex, lastPreviewItemIndex, lastPreviewItem);

		if (
			this.currentIndex === this.currentPreviewItemMedias.length ||
			this.currentPreviewItem?.readerTypeId === ReaderType.FocusWord
		) {
			const currentPreviewItemIndex = this.previewItems.findIndex(
				(previewItem) =>
					previewItem.contentId === this.currentPreviewItem?.contentId &&
					previewItem.readerTypeId === this.currentPreviewItem.readerTypeId
			);

			this.currentPreviewItem = this.previewItems[currentPreviewItemIndex + 1];
			this.assignItemMedias();

			this.currentIndex = 1;
		} else if (this.currentIndex < this.currentPreviewItemMedias.length) {
			this.currentIndex = this.currentIndex + 1;
		}
	}

	disableNextNavigationButton(currentItemIndex: number, lastItemIndex: number, lastItem: PreviewItemContentModel) {
		if (this.currentPreviewItem && currentItemIndex + 1 === lastItemIndex) {
			if (currentItemIndex !== lastItemIndex) {
				if (
					this.hasPreviewItemPages(this.currentPreviewItem) &&
					this.currentIndex + 1 > this.currentPreviewItemMedias.length &&
					this.hasPreviewItemPages(lastItem) &&
					this.getItemMediasLength(lastItem) === 1
				) {
					this.isLastPreviewtItem = true;
				} else if (
					this.hasPreviewItemPages(this.currentPreviewItem) &&
					this.currentIndex === this.currentPreviewItemMedias.length &&
					!this.hasPreviewItemPages(lastItem)
				) {
					this.isLastPreviewtItem = true;
				} else if (
					!this.hasPreviewItemPages(this.currentPreviewItem) &&
					this.hasPreviewItemPages(lastItem) &&
					this.getItemMediasLength(lastItem) === 1
				) {
					this.isLastPreviewtItem = true;
				} else if (!this.hasPreviewItemPages(this.currentPreviewItem) && !this.hasPreviewItemPages(lastItem)) {
					this.isLastPreviewtItem = true;
				}
			}
		} else if (currentItemIndex === lastItemIndex) {
			if (this.currentIndex + 1 >= this.currentPreviewItemMedias.length) {
				this.isLastPreviewtItem = true;
			}
		}
	}

	disablePreviousNavigationButton(
		currentItemIndex: number,
		firstItemIndex: number,
		firstItem: PreviewItemContentModel
	) {
		if (this.currentPreviewItem && currentItemIndex - 1 === firstItemIndex) {
			if (currentItemIndex === 1 && currentItemIndex !== firstItemIndex) {
				if (
					this.hasPreviewItemPages(this.currentPreviewItem) &&
					this.currentIndex === 1 &&
					this.hasPreviewItemPages(firstItem) &&
					this.getItemMediasLength(firstItem) === 1
				) {
					this.isFirstPreviewItem = true;
				} else if (
					this.hasPreviewItemPages(this.currentPreviewItem) &&
					this.currentIndex === 1 &&
					!this.hasPreviewItemPages(firstItem)
				) {
					this.isFirstPreviewItem = true;
				} else if (
					!this.hasPreviewItemPages(this.currentPreviewItem) &&
					this.hasPreviewItemPages(firstItem) &&
					this.getItemMediasLength(firstItem) === 1
				) {
					this.isFirstPreviewItem = true;
				} else if (!this.hasPreviewItemPages(this.currentPreviewItem) && !this.hasPreviewItemPages(firstItem)) {
					this.isFirstPreviewItem = true;
				}
			}
		} else if (currentItemIndex === firstItemIndex && this.currentIndex - 1 <= 1) {
			this.isFirstPreviewItem = true;
		}
	}

	getAbsoluteMediaPath(relativePath: string, containerType: ContainerType) {
		const container = this.downloadSASURL?.find((sas) => sas.type === containerType);
		const containerName = container?.name;
		const pageSasUrl = container?.url || "";
		const splittedPageSasUrl = pageSasUrl.split("?");
		return splittedPageSasUrl[0] + containerName + "/" + relativePath + "?" + splittedPageSasUrl[1];
	}

	assignItemMedias() {
		if (this.currentPreviewItem && this.hasPreviewItemPages(this.currentPreviewItem)) {
			this.currentPreviewItemMedias = this.currentPreviewItem.medias.flatMap((m) =>
				m.pages.map((p) => {
					const media: ReviewItemPagesMedia = {
						pageUrl: this.getAbsoluteMediaPath(p.url, ContainerType.Page),
						audioUrl: this.getAbsoluteMediaPath(p.pageAudioUrl, ContainerType.Audio),
						pageAudioId: p.pageMediaId as string
					};
					return media;
				})
			);
		}
	}

	get readerType() {
		return ReaderType;
	}

	get mediaSlot() {
		return MediaSlot;
	}

	getFocusWordImageUrl(focusWord: FocusWordModel) {
		const relativePath = focusWord.medias.find((f) => f.mediaSlotId === MediaSlot.Image)?.url || "";
		return this.getAbsoluteMediaPath(relativePath, ContainerType.Page);
	}

	public async viewMedia(mediaSlotId: number, mediaUrl?: string, mediaId?: string) {
		let result = false;
		result = this.videoPlayerRef.showModal(mediaId as string, MediaType.MP3, "Media Viewer");

		if (result) {
			setTimeout(() => {
				if (mediaSlotId === MediaSlot.Audio || mediaSlotId === MediaSlot.IndividualAudio) {
					this.videoPlayerRef.setAWSMediaPlayer(
						mediaId as string,
						mediaUrl as string,
						MediaPlayerContentType.mp3
					);
				}
			}, 100);
		}
	}

	getParentContentId() {
		let parentContentId: string | any = null;
		if (!this.parentContentId) {
			parentContentId = this.titleListModule.editedContentId;
		} else {
			parentContentId = this.parentContentId;
		}

		return parentContentId;
	}

	applyOnAllExposures() {
		this.isApplyOnAllLoading = true;
		const parentContentId = this.getParentContentId();

		const items: ContentExposureReaderRequestModel[] = this.selectedItems.map((selectedItem) => {
			return {
				contentExposureId: selectedItem.contentExposureId,
				contentId: selectedItem.contentId,
				readerTypeId: selectedItem.readerTypeId,
				sequence: selectedItem.sequence
			};
		});
		ContentExposureService.applyOnAllExposures(parentContentId, this.selectedLpvId, this.contentExposureId, items)
			.then(() => {
				this.isApplyOnAllLoading = false;
				MessageHelper.Message(
					NotificationType.Success,
					this.formatMessage(ContentExposureManageOrderLocale.CEMOApplyOnAllExposuresSuccessText)
				);
			})
			.catch(() => {
				this.isApplyOnAllLoading = false;
			});
	}

	get isPagesMedia() {
		return (
			this.currentPreviewItem &&
			(this.currentPreviewItem.readerTypeId === ReaderType.Content ||
				this.currentPreviewItem.readerTypeId === ReaderType.ChapterContent)
		);
	}

	hasPreviewItemPages(previewItem: PreviewItemContentModel) {
		return (
			previewItem &&
			(previewItem.readerTypeId === ReaderType.Content || previewItem.readerTypeId === ReaderType.ChapterContent)
		);
	}

	getItemMediasLength(previewItem: PreviewItemContentModel) {
		if (this.hasPreviewItemPages(previewItem)) {
			return previewItem.medias[0].pages.length;
		} else {
			return previewItem.medias.length;
		}
	}

	viewFocusWordMedia(mediaSlotId: MediaSlot, focusWord: FocusWordModel) {
		const media = focusWord.medias.find((media) => media.mediaSlotId === mediaSlotId);
		const url = this.getAbsoluteMediaPath(media?.url as string, ContainerType.Audio);
		this.viewMedia(mediaSlotId, url, media?.id);
	}
}
